import { Flex } from "@chakra-ui/react";
import { Section, type SectionProps } from "components/section";
import { SecurityBadges, type SecurityBadgesProps } from "components/security-badges";
import { type Theme } from "contexts/color-theme";
import React from "react";
import { remCalc } from "theme/utils";
import { TextTitle, type TextTitleProps } from "ui/text";

type SecurityFeaturesProps = {
  /**
   * The color theme to use for text and badge colors.
   * @default "light"
   */
  theme?: Theme;

  /** Override the default title element.
   * @default "h2"
   */
  titleAs?: TextTitleProps["titleAs"];

  /**
   * Sets a custom title for the section.
   */
  title?: string;

  /**
   * Sets a custom description for the section.
   */
  description?: string;

  /**
   * Set a custom max width for the text content.
   * @default remCalc(740)
   */
  textMaxW?: string;

  /**
   * The flex direction of the component with text & badges.
   * @default "column"
   */
  direction?: "column" | "row";
} & SectionProps &
  Pick<SecurityBadgesProps, "hasHipaa">;

export function SecurityFeatures({
  theme = "light",
  titleAs,
  title = "We don’t store your data",
  description = "Hightouch is more secure because no duplicate storage is required. Instead, we simply read from your data warehouse, where your data stays safe and sound.",
  textMaxW = remCalc(740),
  direction = "column",
  hasHipaa = true,
  ...sectionProps
}: SecurityFeaturesProps) {
  const bgColor = theme === "dark" ? "white" : "new.gray.50";

  return (
    <Section theme={theme} {...sectionProps}>
      <Flex align="center" flexDir={{ base: "column", lg: direction }} gap="biggest">
        {(title || description) && (
          <TextTitle
            maxW={textMaxW}
            theme={theme}
            size="sm"
            align={direction === "row" ? "start" : "center"}
            title={title}
            titleAs={titleAs}
            description={description}
          />
        )}

        <SecurityBadges
          hasHipaa={hasHipaa}
          bgColor={bgColor}
          // Override default styles for row layout
          sizes={direction === "row" ? { base: "84px", md: "92px", xl: "112px" } : undefined}
          gap={direction === "row" ? "small" : undefined}
          mx="unset"
        />
      </Flex>
    </Section>
  );
}
